import { type RefObject, useEffect } from 'react';

/**
 * Keeps the active tab centered in the horizontally scrolling tab strip: after the active tab
 * changes or the tabs move, smoothly scrolls the container so the
 * `[data-panel-tab-active="true"]` tab is centered.
 */
export function useActiveTabScroll(params: {
  activeId: string | null;
  tabScrollRef: RefObject<HTMLDivElement | null>;
  tabs: readonly unknown[];
}): void {
  const { activeId, tabScrollRef, tabs } = params;
  // biome-ignore lint/correctness/useExhaustiveDependencies(tabs): tabs that moved move the active one: it is centered again.
  useEffect(() => {
    if (activeId === null) return;
    const scrollContainer = tabScrollRef.current;
    if (scrollContainer === null) return;
    const activeTab = scrollContainer.querySelector<HTMLElement>('[data-panel-tab-active="true"]');
    if (activeTab === null) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    scrollContainer.scrollTo({
      behavior: 'smooth',
      left:
        scrollContainer.scrollLeft +
        tabRect.left -
        containerRect.left -
        (containerRect.width - tabRect.width) / 2,
    });
  }, [activeId, tabScrollRef, tabs]);
}
